import React, { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import type { Action, ActionImpl, KBarState } from 'kbar';
import {
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarResults,
  KBarSearch,
  useKBar,
  useMatches,
  useRegisterActions
} from 'kbar';
import isString from 'lodash/isString';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import { typeStyle } from 'constants/typography';
import useProjectNavigation from 'hooks/kbar/project-navigation.hooks';
import useModulesSearchActions from 'hooks/kbar/use-modules-actions.hooks';
import useTemplatesSearchActions from 'hooks/kbar/use-templates-kbar-actions.hooks';
import useBasicNavigationActions from 'hooks/kbar/basic-navigation-actions.hooks';
import useOrganizationSettingsTabsActions from 'hooks/kbar/use-settings-tabs-actions.hooks';
import { EventNames, track } from 'utils/analytics.utils';
import type { ActionAnalyticsData } from 'utils/kbar.utils';
import { createActionToDataMap } from 'utils/kbar.utils';
import { useEnvironmentSearchActions } from 'hooks/kbar/use-environment-search-actions.hooks';
import { KbarResultItem } from 'components/kbar/kbar-result-item';
import uniqBy from 'lodash/uniqBy';
import Loader from 'components/common/loader';

let lastVisualState = 'hidden';

const trackKbarState = (state: KBarState) => {
  const visualState = state.visualState;

  if (visualState === 'showing' && lastVisualState !== 'showing') {
    track(EventNames.CMD_K_OPENED);
  }

  lastVisualState = visualState;
  return { searchQuery: state.searchQuery, isKbarOpen: ['showing', 'animating-in'].includes(visualState) };
};

export const KbarWrapper: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const { searchQuery, isKbarOpen } = useKBar(trackKbarState);
  const isSearching = searchQuery.length > 0;
  const basicActions = useBasicNavigationActions();
  const settingsTabsActions = useOrganizationSettingsTabsActions();
  const { actions: projectsActions, isProjectsReady, projectsError, refetchProjects } = useProjectNavigation();
  const {
    actions: environmentActions,
    isLoadingSearch,
    environmentsError
  } = useEnvironmentSearchActions({
    searchTerm: searchQuery,
    enabled: isKbarOpen && isSearching
  });
  const {
    actions: templateActions,
    isLoading: isLoadingTemplates,
    error: templatesError
  } = useTemplatesSearchActions({
    enabled: isKbarOpen && isSearching && isProjectsReady && !isLoadingSearch
  });
  const {
    actions: moduleActions,
    isLoading: isLoadingModules,
    error: modulesError
  } = useModulesSearchActions({
    enabled: isKbarOpen && isSearching && isProjectsReady && !isLoadingSearch
  });
  // The templates/modules queries keep their cached error while disabled, so count them only during
  // an active search. environmentsError self-clears (useAsyncPromise resets per run) and the tree
  // query is always enabled, so those two are current whenever set.
  const hasSearchError = Boolean(
    environmentsError || projectsError || (isSearching && (templatesError || modulesError))
  );

  // A failed tree query never recovers on its own: the global react-query config doesn't retry or
  // refetch on focus, and this component never unmounts. Retry at most once per palette open — the
  // ref covers an error that only lands after the palette is already open, and stops a still-failing
  // refetch (a new error object per attempt) from looping.
  const hasRetriedTreeFetchRef = useRef(false);
  useEffect(() => {
    if (!isKbarOpen) {
      hasRetriedTreeFetchRef.current = false;
      return;
    }
    if (projectsError && !hasRetriedTreeFetchRef.current) {
      hasRetriedTreeFetchRef.current = true;
      refetchProjects();
    }
  }, [isKbarOpen, projectsError, refetchProjects]);
  const nonProjectSearchActions = useMemo(
    () => [...environmentActions, ...templateActions, ...moduleActions],
    [environmentActions, templateActions, moduleActions]
  );

  const isBlockingSearchResults =
    isSearching && (isLoadingSearch || isLoadingTemplates || isLoadingModules || !isProjectsReady);

  const isLoadingResults = isBlockingSearchResults;

  const allHooksAction = useMemo(() => {
    if (isBlockingSearchResults) return [];

    return uniqBy([...basicActions, ...projectsActions, ...nonProjectSearchActions, ...settingsTabsActions], 'id');
  }, [basicActions, isBlockingSearchResults, nonProjectSearchActions, projectsActions, settingsTabsActions]);

  const actionAnalyticsMap = useMemo(() => createActionToDataMap(allHooksAction), [allHooksAction]);
  const wrappedActions = useMemo(
    () => warpActionsWithAnalytics(allHooksAction, actionAnalyticsMap),
    [allHooksAction, actionAnalyticsMap]
  );
  useRegisterActions(wrappedActions, [wrappedActions, actionAnalyticsMap]);

  return (
    <>
      <KbarPalette isLoading={isLoadingResults} hasError={hasSearchError} />
      {children}
    </>
  );
};

/**
 * The palette's chrome, without the six action hooks above it - each of those reads the API, and a story
 * must render with no network (frontend/CLAUDE.md). `SearchResults` needs only kbar's own context, so this
 * whole subtree renders under a bare `KBarProvider`. Same DOM, same `data-e2e`.
 */
export const KbarPalette: React.FC<{ isLoading?: boolean; hasError?: boolean }> = ({
  isLoading = false,
  hasError = false
}) => (
  <KBarPortal>
    <StyledKbarPositioner>
      <StyledAnimator>
        <StyledSearch data-e2e={`kbar-search`} />
        <SearchResults isLoading={isLoading} hasError={hasError} />
      </StyledAnimator>
    </StyledKbarPositioner>
  </KBarPortal>
);

const warpActionsWithAnalytics = (actions: Action[], actionToDataMap: Record<string, ActionAnalyticsData>) => {
  return actions.map(action => {
    if (!action.perform) return action;

    const analyticsPerform: Action['perform'] = (currentActionImpl: ActionImpl) => {
      track(EventNames.CMD_K_ITEM_CLICKED, actionToDataMap[action.id]);
      action!.perform!(currentActionImpl);
    };
    return { ...action, perform: analyticsPerform };
  });
};

/* kbar's default maxHeight is a bare 400px with no relation to the row height, so the ~44px rows
   (4px margin + 8px padding each side + 20px content) clipped the tenth row mid-glyph inside the
   rounded card (UI-polish review). Eight whole rows instead. */
const NEW_DESIGN_ROW_HEIGHT_PX = 44;
const NEW_DESIGN_RESULTS_MAX_HEIGHT_PX = NEW_DESIGN_ROW_HEIGHT_PX * 8;

const SearchResults = ({ isLoading, hasError }: { isLoading: boolean; hasError: boolean }) => {
  const { results, rootActionId } = useMatches();
  // Registration runs in a useEffect, which fires after commit. The render right after `isLoading`
  // flips false can still see kbar's previous (often empty, unregistered-yet) action map, painting
  // "no results" for a frame - or up to useMatches' 100ms throttle - before the real matches land.
  // Requiring at least one registered action tells "not yet registered" apart from "genuinely
  // matched nothing", where basic/settings actions are always registered.
  const { actionCount } = useKBar(state => ({ actionCount: Object.keys(state.actions).length }));

  if (isLoading) {
    return (
      <LoadingContainer>
        <Loader size={'small'} data-e2e={'kbar-loader'} />
      </LoadingContainer>
    );
  }

  // APO-614: an error, an empty org and a term matching nothing used to render the same blank list.
  // Partial results beat an error screen, so the list still renders whenever there is one to show.
  return (
    <>
      {hasError && (
        <MessageContainer data-e2e={'kbar-error'}>
          <FormattedMessage id={'kbar.search.error'} />
        </MessageContainer>
      )}
      {results.length === 0 && !hasError && actionCount > 0 ? (
        <MessageContainer data-e2e={'kbar-no-results'}>
          <FormattedMessage id={'kbar.search.no-results'} />
        </MessageContainer>
      ) : (
        <KBarResults
          data-e2e={'kbar-results'}
          maxHeight={NEW_DESIGN_RESULTS_MAX_HEIGHT_PX}
          items={results}
          onRender={({ item, active }) =>
            isString(item) ? (
              <SectionName data-e2e={`kbar-action-section-${item}`}>{item}</SectionName>
            ) : (
              <KbarResultItem action={item} active={active} currentRootActionId={rootActionId as string} />
            )
          }
        />
      )}
    </>
  );
};

/**
 * The palette has no Figma frame of its own (APO-471), so it extrapolates from the tokens the antd bridge
 * already gives a card and a form field - `fieldComponent` in `constants/antd.constants`.
 */
const SectionName = styled.div`
  ${({ theme }) => css`
    padding: ${theme.padding.xs} ${theme.padding.lg};
    /* Overline is the ramp's uppercase section label, so it carries the text-transform too. */
    ${typeStyle.overline}
    color: var(--text-secondary);
    background: var(--surface-main-page);
  `}
`;

const StyledSearch = styled(KBarSearch)`
  width: 100%;
  max-width: 600px;
  box-sizing: border-box;
  outline: none;
  border: none;

  ${({ theme }) => css`
    padding: ${theme.padding.md} ${theme.padding.lg};
    ${typeStyle.labelMd}
    background: var(--surface-main-cards);
    color: var(--text-heading);
    border-bottom: ${theme.borderWidth.xs} solid var(--border-primary);

    &::placeholder {
      color: var(--text-secondary);
    }
  `}
`;

const StyledAnimator = styled(KBarAnimator)`
  max-width: 600px;
  width: 100%;

  ${({ theme }) => css`
    background-color: var(--surface-main-cards);
    border: ${theme.borderWidth.xs} solid var(--border-primary);
    border-radius: ${theme.radius['300']};
    box-shadow: var(--shadow-modal);
    overflow: hidden;
  `}
`;

const StyledKbarPositioner = styled(KBarPositioner)`
  background-color: ${({ theme }) => theme.kbarScrim};

  /* At 99 the palette sits under every antd popup, whose base is zIndexPopupBase 1000. */
  ${({ theme }) => css`
    z-index: ${theme.zIndex.modal};
  `}
`;

const LoadingContainer = styled.div`
  padding: ${({ theme }) => theme.padding.lg};
  height: 120px;
`;

const MessageContainer = styled.div`
  padding: ${({ theme }) => theme.padding.lg};

  ${typeStyle.labelMd}
  color: var(--text-secondary);
`;
